import type { ScopeRef, ScopeTreeResponse } from "./types";

export function isScopeAvailable(scope: ScopeRef, tree: ScopeTreeResponse): boolean {
	switch (scope.type) {
		case "org":
			return tree.orgVisible;
		case "wlasne":
			return tree.wlasne.idfirms.length > 0;
		case "franczyza":
			return tree.franczyzaIdfirms.length > 0;
		case "region":
			return tree.wlasne.regions.some((r) => r.name === scope.id);
		case "spolka":
			return tree.spolki.some((s) => s.nip === scope.id);
		case "apteka":
			return tree.wlasne.idfirms.includes(scope.id) || tree.franczyzaIdfirms.includes(scope.id);
		case "orphan":
			return tree.orphanGroups.some((o) => o.nip === scope.id);
		case "kr":
			return tree.kierownicy.some((k) => k.name === scope.id);
	}
}

/**
 * Fallback scope dla raportow tabelarycznych (Raport produktowy), gdzie
 * oczekiwany jest bilans CALEJ widocznej sieci — nie pojedyncza apteka.
 *
 * Rozni sie od `pickDefaultScope`: konto BEZ aptek wlasnych dostaje rollup
 * "Franczyza" (wszystkie widoczne apteki franczyzowe), a nie pierwsza
 * pojedyncza apteke. `pickDefaultScope` celowo wybiera 1 apteke dla paneli
 * bocznych (perf — nie laduj calej sieci), ale raport produktowy = bilans
 * zakupu vs sprzedazy calej sieci, parytet ze starym raportem "Zakupy
 * indeksow" (ktory filtruje tylko po widocznych idfirm, bez zawezania scope).
 *
 * Bez tego franczyzobiorca (0 aptek wlasnych) widzial raport zawezony do
 * jednej apteki i gubil produkty kupione w POZOSTALYCH swoich aptekach
 * (incydent: BLOZ 3186922 Auropect — obecny w 22 z 272 aptek, ale nie w
 * domyslnej pierwszej).
 *
 * Zwraca `null` gdy biezacy scope jest OK (brak przelaczenia).
 */
export function productReportFallbackScope(
	tree: ScopeTreeResponse,
	currentScopeType: ScopeRef["type"],
): ScopeRef | null {
	const hasWlasne = tree.wlasne.idfirms.length > 0;
	const hasFranczyza = tree.franczyzaIdfirms.length > 0;
	if (currentScopeType === "wlasne" && !hasWlasne && hasFranczyza) {
		return { type: "franczyza" };
	}
	if (currentScopeType === "franczyza" && !hasFranczyza && hasWlasne) {
		return { type: "wlasne" };
	}
	return null;
}

/**
 * Konto z DOKLADNIE jedna widoczna apteka (wlasna lub franczyzowa) → scope tej
 * apteki. Rollup ("Wlasne" / "Franczyza" / spolka) nad jedna apteka zwraca te
 * same dane, ale pokazuje bezuzyteczna etykiete grupy zamiast nazwy apteki.
 *
 * `null` przy 0 lub >1 aptekach — wtedy obowiazuje dotychczasowa kolejnosc
 * fallbackow (patrz `pickDefaultScope`).
 */
export function singlePharmacyScope(tree: ScopeTreeResponse): ScopeRef | null {
	const idfirms = new Set([...tree.wlasne.idfirms, ...tree.franczyzaIdfirms]);
	if (idfirms.size !== 1) return null;
	const [idfirm] = idfirms;
	return { type: "apteka", id: idfirm };
}

/**
 * Pierwsza apteka widoczna dla uzytkownika, w kolejnosci alfabetycznej z
 * `/scope/tree` (wlasne → spolki → bez spolki). `null`, gdy konto nie ma
 * zadnej apteki.
 *
 * Uzywane jako korekta, gdy zapisany scope wypadl z uprawnien: rollup
 * („Wlasne" / „Franczyza") wyswietlalby wtedy etykiete grupy, ktorej user nie
 * rozpoznaje, a franczyzobiorca bez aptek wlasnych — pusty ekran.
 */
export function firstPharmacyScope(tree: ScopeTreeResponse): ScopeRef | null {
	const ordered = [
		...tree.wlasne.regions.flatMap((r) => r.pharmacies),
		...tree.spolki.flatMap((s) => s.pharmacies),
		...tree.orphanGroups.flatMap((o) => o.pharmacies),
	];
	const idfirm = ordered[0]?.idfirm ?? tree.wlasne.idfirms[0] ?? tree.franczyzaIdfirms[0];
	return idfirm == null ? null : { type: "apteka", id: idfirm };
}

export function pickDefaultScope(tree: ScopeTreeResponse): ScopeRef | null {
	const single = singlePharmacyScope(tree);
	if (single) return single;
	if (tree.wlasne.idfirms.length > 0) return { type: "wlasne" };
	// Franczyzobiorca bez Wlasne: pelny rollup "Franczyza", NIE pierwsza
	// pojedyncza apteka. Wczesniejszy wariant (pierwsza apteka, perf) zawezal
	// KAZDY widok tabelaryczny do 1 apteki i gubil produkty z pozostalych
	// aptek usera (incydent BLOZ 3186922 Auropect — obecny w 22 z 272 aptek,
	// ale nie w domyslnej pierwszej). Parytet ze starymi raportami, ktore
	// filtruja tylko po visible_idfirms.
	if (tree.franczyzaIdfirms.length > 0) return { type: "franczyza" };
	if (tree.spolki.length > 0) return { type: "spolka", id: tree.spolki[0].nip };
	if (tree.orphanGroups.length > 0) return { type: "orphan", id: tree.orphanGroups[0].nip };
	if (tree.orgVisible) return { type: "org" };
	return null;
}

/**
 * Ownership scope'a — czy scope zawiera apteki wlasne / franczyzowe.
 * Uzywane do chowania opcji per grupa aptek (np. atrybuty zalegacza:
 * 3002 tylko franczyza, 3003 tylko wlasne). Brak drzewa / scope
 * mieszany (org) → oba true (pokazuj wszystko).
 */
export function scopeOwnership(
	scope: ScopeRef,
	tree?: ScopeTreeResponse,
): { hasOwn: boolean; hasFranchise: boolean } {
	switch (scope.type) {
		case "wlasne":
		case "region":
			return { hasOwn: true, hasFranchise: false };
		case "franczyza":
		case "spolka":
		case "orphan":
			return { hasOwn: false, hasFranchise: true };
		case "apteka": {
			if (!tree) return { hasOwn: true, hasFranchise: true };
			const own = tree.wlasne.idfirms.includes(scope.id);
			return { hasOwn: own, hasFranchise: !own };
		}
		default:
			return { hasOwn: true, hasFranchise: true };
	}
}

/**
 * Idfirm apteki, ktorym WOLNO strzelic w endpoint per-apteka (403 zamiast
 * pustej listy, gdy apteka jest poza uprawnieniami).
 *
 * `null` dopoki `/scope/tree` nie potwierdzi, ze apteka jest widoczna. Zapis
 * scope'u w localStorage przezywa zmiane uprawnien i przelaczenie konta
 * (impersonacja), a `tree` jest juz przeciete z `visible_idfirms` po stronie
 * backendu — patrz `routes/scope.rs`. Bez tej bramki panel wysylal
 * `?idfirm=<cudza apteka>` i lapal 403 (Sentry FARMASTER-FRONTEND-3H).
 */
export function scopedAptekaIdfirm(scope: ScopeRef, tree?: ScopeTreeResponse): number | null {
	if (scope.type !== "apteka") return null;
	if (!tree) return null;
	return isScopeAvailable(scope, tree) ? scope.id : null;
}
